import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopNav from "../components/TopNav";
import Orbs from "../components/Orbs";
import { Section, Overline } from "../components/Layout";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";

// Per-environment: staging events is launched from foundry-dev, production
// from foundry. Set at BUILD time (see Dockerfile / render.yaml).
const FOUNDRY_URL = process.env.REACT_APP_FOUNDRY_URL || "https://foundry.venturedock.com";

/**
 * Landing target of the Foundry SSO handoff (M2): vdock-sync redirects here as
 * /sso#<token>. The token rides the URL FRAGMENT so it never reaches server
 * logs; we read it once, exchange it for a local admin session, and scrub it
 * from the address bar before navigating into the admin.
 *
 * THEME. Foundry and this app are different ORIGINS, so they cannot share a
 * localStorage theme however identically they spell the key — a user in dark
 * mode on Foundry was landing here on whatever this origin last stored. The
 * launcher therefore appends ?theme=dark|light to the redirect and we adopt it
 * on arrival. It rides the query string, not the fragment: it is a display
 * preference, not a secret, and the fragment is reserved for the token.
 */
export default function SsoCallback() {
  const { ssoExchange } = useAuth();
  const { setTheme } = useTheme();
  const nav = useNavigate();
  const [error, setError] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke guard: the token is single-use in spirit
    ran.current = true;
    const token = window.location.hash.replace(/^#/, "");
    const handedTheme = new URLSearchParams(window.location.search).get("theme");
    if (handedTheme === "dark" || handedTheme === "light") setTheme(handedTheme);
    window.history.replaceState(null, "", "/sso");
    if (!token) {
      setError("No sign-in token — launch Events Admin from Foundry.");
      return;
    }
    ssoExchange(token)
      .then(() => nav("/admin/community", { replace: true }))
      .catch((err) => setError(err.response?.data?.detail || "Sign-in failed — relaunch from Foundry."));
  }, [ssoExchange, nav]);

  return (
    <div className="relative min-h-screen" data-testid="sso-page">
      <Orbs />
      <div className="relative z-10">
        <TopNav />
        <Section className="pt-20 pb-24" innerClassName="max-w-md mx-auto">
          <div className="glass-strong rounded-3xl p-8 text-center">
            <Overline>Admin</Overline>
            {error ? (
              <>
                <h1 className="mt-3 text-2xl font-heading font-medium tracking-tight">Sign-in failed</h1>
                <p className="text-muted-foreground text-sm mt-2">{error}</p>
                <a href={FOUNDRY_URL} className="btn-primary w-full inline-block mt-6" data-testid="sso-foundry-link">
                  Open Foundry
                </a>
              </>
            ) : (
              <h1 className="mt-3 text-2xl font-heading font-medium tracking-tight">Signing you in…</h1>
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}
